import React, { FC } from "react";

import { useLocale } from "@adsk/alloy-react-locale";

import i18n from "../../i18n";
import {
  clampPercent,
  PercentCompleteDisplay,
  StatusNameDisplay,
  type StatusNameSize,
  StatusWithProgressRow,
} from "./StatusProgressRowParts";

export type StatusWithProgressDisplayStatusNameSize = StatusNameSize;

export type StatusWithProgressDisplayProps = {
  statusColor?: string;
  statusName?: string;
  /** When null or undefined, progress bar and percent label are omitted. */
  percent?: number | null;
  isUnspecified?: boolean;
  /**
   * When true, suppresses the horizontal `ProgressBar` row and renders only
   * the status color stripe, status name, and "N% completed" text. Used by
   * dense list surfaces (e.g. acc-viewer's `TrackableAssetList`) where the
   * progress bar is redundant in a row. Default rendering for the asset
   * detail panel is unchanged.
   */
  hideProgressBar?: boolean;
  /**
   * Controls the typography size of the status name. Defaults to `"medium"`
   * (detail panel). Use `"small"` in dense lists such as the reference picker
   * entity row.
   */
  statusNameSize?: StatusWithProgressDisplayStatusNameSize;
};

const StatusWithProgressDisplay: FC<StatusWithProgressDisplayProps> = ({
  statusColor,
  statusName,
  percent,
  isUnspecified,
  hideProgressBar,
  statusNameSize = "medium",
}) => {
  const { T } = useLocale(i18n);
  const safePercent = percent != null ? clampPercent(percent) : null;
  const unspecified = Boolean(isUnspecified) || !statusName;
  const displayName = unspecified
    ? T.translate("placeholders.unspecified")?.toString()
    : statusName;

  return (
    <StatusWithProgressRow data-testid="StatusWithProgressDisplay">
      <StatusNameDisplay
        statusColor={statusColor}
        displayName={displayName}
        isUnspecified={unspecified}
        statusNameSize={statusNameSize}
      />
      {safePercent != null && (
        <PercentCompleteDisplay
          percent={safePercent}
          hideProgressBar={hideProgressBar}
        />
      )}
    </StatusWithProgressRow>
  );
};

export default StatusWithProgressDisplay;
